import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/utils/format';
import Modal from './Modal';
import type { AdminClient } from '@/mocks/adminClients';
import type { AdminRequest } from '@/mocks/adminRequests';
import type { BlockedIp } from '@/hooks/useAdminData';

type ManagementTabProps = {
  clients: AdminClient[];
  requests: AdminRequest[];
  blockedIps: BlockedIp[];
  managerEmail: string;
  onDeleteClient: (id: string, manager: string) => Promise<void>;
  onPurgeAllClients: (manager: string) => Promise<void>;
  onDeleteRequest: (id: string, manager: string) => Promise<void>;
  onPurgeAllRequests: (manager: string) => Promise<void>;
  onDeleteIp: (id: string, manager: string) => Promise<void>;
  onPurgeBlockedIps: (manager: string) => Promise<void>;
};

type ConfirmAction = {
  title: string;
  message: string;
  confirmLabel: string;
  run: () => Promise<void>;
};

export default function ManagementTab({
  clients,
  requests,
  blockedIps,
  managerEmail,
  onDeleteClient,
  onPurgeAllClients,
  onDeleteRequest,
  onPurgeAllRequests,
  onDeleteIp,
  onPurgeBlockedIps,
}: ManagementTabProps) {
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(null), 4000);
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    await confirm.run();
    setBusy(false);
    setConfirm(null);
  };

  const dangerButton =
    'inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-3.5 py-2.5 text-sm font-semibold text-accent-900 transition-colors hover:bg-accent-100';
  const softButton =
    'inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100';

  const sections = [
    {
      key: 'clients',
      icon: 'ri-user-3-line',
      title: t('admin.management.clientsTitle'),
      subtitle: t('admin.management.clientsSubtitle', { count: clients.length }),
      actions: [
        {
          label: t('admin.management.purgeClients'),
          tone: 'danger',
          onClick: () =>
            setConfirm({
              title: t('admin.management.confirmClientsTitle'),
              message: t('admin.management.confirmClientsText', { count: clients.length }),
              confirmLabel: t('admin.management.confirmButton'),
              run: async () => {
                await onPurgeAllClients(managerEmail);
                flash(t('admin.management.clientsPurged'));
              },
            }),
        },
      ],
      items: clients.map((client) => ({
        id: client.id,
        primary: `${client.accessCode} · ${client.fullName}`,
        secondary: client.email || t('admin.management.noEmail'),
        onRemove: () =>
          setConfirm({
            title: t('admin.management.confirmClientTitle'),
            message: t('admin.management.confirmClientText', {
              name: `${client.fullName} (${client.accessCode})`,
            }),
            confirmLabel: t('admin.management.confirmButton'),
            run: async () => {
              await onDeleteClient(client.id, managerEmail);
              flash(t('admin.management.clientDeleted'));
            },
          }),
      })),
    },
    {
      key: 'requests',
      icon: 'ri-key-2-line',
      title: t('admin.management.requestsTitle'),
      subtitle: t('admin.management.requestsSubtitle', { count: requests.length }),
      actions: [
        {
          label: t('admin.management.purgeRequests'),
          tone: 'danger',
          onClick: () =>
            setConfirm({
              title: t('admin.management.confirmRequestsTitle'),
              message: t('admin.management.confirmRequestsText', { count: requests.length }),
              confirmLabel: t('admin.management.confirmButton'),
              run: async () => {
                await onPurgeAllRequests(managerEmail);
                flash(t('admin.management.requestsPurged'));
              },
            }),
        },
      ],
      items: requests.map((request) => ({
        id: request.id,
        primary: `${request.reference} · ${request.clientName || request.fullName}`,
        secondary: formatDateTime(request.createdAt),
        onRemove: () =>
          setConfirm({
            title: t('admin.management.confirmRequestTitle'),
            message: t('admin.management.confirmRequestText', { name: request.reference }),
            confirmLabel: t('admin.management.confirmButton'),
            run: async () => {
              await onDeleteRequest(request.id, managerEmail);
              flash(t('admin.management.requestDeleted'));
            },
          }),
      })),
    },
    {
      key: 'ips',
      icon: 'ri-global-line',
      title: t('admin.management.ipsTitle'),
      subtitle: t('admin.management.ipsSubtitle', { count: blockedIps.length }),
      actions: [
        {
          label: t('admin.management.purgeIps'),
          tone: 'danger',
          onClick: () =>
            setConfirm({
              title: t('admin.management.confirmIpsTitle'),
              message: t('admin.management.confirmIpsText', { count: blockedIps.length }),
              confirmLabel: t('admin.management.confirmButton'),
              run: async () => {
                await onPurgeBlockedIps(managerEmail);
                flash(t('admin.management.ipsPurged'));
              },
            }),
        },
      ],
      items: blockedIps.map((entry) => ({
        id: entry.id,
        primary: entry.ip,
        secondary: entry.reason,
        onRemove: () =>
          setConfirm({
            title: t('admin.management.confirmIpTitle'),
            message: t('admin.management.confirmIpText', { name: entry.ip }),
            confirmLabel: t('admin.management.confirmButton'),
            run: async () => {
              await onDeleteIp(entry.id, managerEmail);
              flash(t('admin.management.ipDeleted'));
            },
          }),
      })),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-xl font-semibold text-foreground-950">
          {t('admin.management.title')}
        </h1>
        <p className="mt-1 text-sm text-foreground-600">{t('admin.management.subtitle')}</p>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-900">
        <i className="ri-alert-line mt-0.5"></i>
        <span>{t('admin.management.warning')}</span>
      </div>

      {notice && (
        <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-900">
          <i className="ri-checkbox-circle-line mt-0.5"></i>
          <span>{notice}</span>
        </div>
      )}

      {sections.map((section) => (
        <section
          key={section.key}
          className="rounded-lg border border-background-200 bg-background-50"
        >
          <div className="flex flex-col gap-3 border-b border-background-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-700">
                <i className={`${section.icon} text-lg`}></i>
              </span>
              <div>
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  {section.title}
                </h2>
                <p className="mt-0.5 text-xs text-foreground-500">{section.subtitle}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {section.actions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  onClick={action.onClick}
                  className={action.tone === 'danger' ? dangerButton : softButton}
                >
                  <i className={action.tone === 'danger' ? 'ri-delete-bin-6-line' : 'ri-eraser-line'}></i>
                  {action.label}
                </button>
              ))}
            </div>
          </div>

          {section.items.length === 0 ? (
            <p className="px-5 py-6 text-sm text-foreground-500">{t('admin.management.empty')}</p>
          ) : (
            <ul className="max-h-72 divide-y divide-background-100 overflow-y-auto">
              {section.items.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground-900">
                      {item.primary}
                    </p>
                    <p className="truncate text-xs text-foreground-500">{item.secondary}</p>
                  </div>
                  <button
                    type="button"
                    onClick={item.onRemove}
                    aria-label={t('admin.management.delete')}
                    className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-900"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}

      {confirm && (
        <Modal title={confirm.title} onClose={() => (busy ? undefined : setConfirm(null))}>
          <p className="text-sm leading-relaxed text-foreground-700">{confirm.message}</p>
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setConfirm(null)}
              disabled={busy}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t('admin.management.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void runConfirm()}
              disabled={busy}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
            >
              {busy && <i className="ri-loader-4-line animate-spin"></i>}
              {confirm.confirmLabel}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}